import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  User,
  CurrencyDollar,
  Gear,
  CheckCircle,
  Spinner,
  Airplane,
  CalendarBlank,
  UserSwitch,
  Receipt,
  Percent,
  Truck,
  Key
} from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user, updateCurrency, token } = useAuth();
  const [selectedCurrency, setSelectedCurrency] = useState(user?.preferred_currency || 'USD');
  const [saving, setSaving] = useState(false);
  
  // Out of Office state
  const [oooLoading, setOooLoading] = useState(false);
  const [oooSaving, setOooSaving] = useState(false);
  const [oooActive, setOooActive] = useState(false);
  const [oooStartDate, setOooStartDate] = useState('');
  const [oooEndDate, setOooEndDate] = useState('');
  const [oooDelegate, setOooDelegate] = useState('');
  const [oooReason, setOooReason] = useState('');

  // Change-my-password state
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNew, setCpNew] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpSaving, setCpSaving] = useState(false);

  const handleChangeMyPassword = async (e) => {
    e.preventDefault();
    if (cpNew.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (cpNew !== cpConfirm) { toast.error('New password + confirmation do not match'); return; }
    if (cpNew === cpCurrent) { toast.error('New password must differ from current'); return; }
    setCpSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-my-password`,
        { current_password: cpCurrent, new_password: cpNew },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password updated. Use it next time you sign in.');
      setCpCurrent(''); setCpNew(''); setCpConfirm('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change password');
    } finally {
      setCpSaving(false);
    }
  };

  const [delegates, setDelegates] = useState([]);

  // Tax Settings state
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxSaving, setTaxSaving] = useState(false);
  const [taxType, setTaxType] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [taxOnByDefault, setTaxOnByDefault] = useState(true);

  // Freight Allocation Settings state
  const [freightMethod, setFreightMethod] = useState('by_value');
  const [weightUnit, setWeightUnit] = useState('kg');
  const [volumeUnit, setVolumeUnit] = useState('cbm');

  // Pilot Mode state
  const [pilotEnabled, setPilotEnabled] = useState(false);
  const [pilotDepartment, setPilotDepartment] = useState('');
  const [pilotDepartmentOptions, setPilotDepartmentOptions] = useState([]);
  const [pilotSaving, setPilotSaving] = useState(false);
  const isHotelAdmin = ['purchasing_manager', 'system_admin'].includes(user?.role);

  // Check if user is an approver (PM, DH, FC)
  const isApprover = ['purchasing_manager', 'department_head', 'financial_controller'].includes(user?.role);
  const canEditTaxSettings = ['purchasing_manager', 'financial_controller'].includes(user?.role);

  // Fetch OOO settings and delegates on mount
  useEffect(() => {
    if (isApprover) {
      fetchOOOSettings();
      fetchDelegates();
    }
    if (canEditTaxSettings) {
      fetchTaxSettings();
    }
    if (isHotelAdmin) {
      fetchPilotMode();
    }
  }, [isApprover, canEditTaxSettings, isHotelAdmin]);

  const fetchTaxSettings = async () => {
    if (!user?.property_ids?.[0]) return;
    setTaxLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/property-settings/${user.property_ids[0]}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setTaxType(data.tax_type || '');
      setTaxRate(data.tax_rate ? data.tax_rate.toString() : '');
      setTaxOnByDefault(data.tax_on_by_default !== false);
      setFreightMethod(data.freight_allocation_method || 'by_value');
      setWeightUnit(data.weight_unit || 'kg');
      setVolumeUnit(data.volume_unit || 'cbm');
      setPilotEnabled(Boolean(data.pilot_enabled));
      setPilotDepartment(data.pilot_department || '');
    } catch (error) {
      console.error('Failed to fetch tax settings:', error);
    } finally {
      setTaxLoading(false);
    }
  };

  const fetchPilotMode = async () => {
    if (!user?.property_ids?.[0]) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/property-settings/${user.property_ids[0]}/pilot-mode`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPilotEnabled(Boolean(res.data.pilot_enabled));
      setPilotDepartment(res.data.pilot_department || '');
      setPilotDepartmentOptions(res.data.departments || []);
    } catch (e) {
      console.error('Failed to fetch pilot mode:', e);
    }
  };

  const handleSavePilot = async (nextEnabled, nextDept) => {
    if (!user?.property_ids?.[0]) {
      toast.error('No property assigned');
      return;
    }
    if (nextEnabled && !nextDept) {
      toast.error('Select a department before enabling Pilot Mode');
      return;
    }
    setPilotSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/property-settings/${user.property_ids[0]}/pilot-mode`,
        { pilot_enabled: nextEnabled, pilot_department: nextDept || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPilotEnabled(nextEnabled);
      setPilotDepartment(nextDept || '');
      toast.success(nextEnabled ? `Pilot Mode enabled — ${nextDept}` : 'Pilot Mode disabled');
      // Trigger a global event so banners + lists can refresh
      window.dispatchEvent(new CustomEvent('pilot-mode-changed', { detail: { enabled: nextEnabled, department: nextDept || null } }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Pilot Mode');
    } finally {
      setPilotSaving(false);
    }
  };

  const handleSaveTaxSettings = async () => {
    if (!user?.property_ids?.[0]) {
      toast.error('No property assigned');
      return;
    }
    
    const rate = parseFloat(taxRate);
    if (taxRate && (isNaN(rate) || rate < 0 || rate > 100)) {
      toast.error('Tax rate must be between 0 and 100');
      return;
    }
    
    setTaxSaving(true);
    try {
      await axios.put(`${API_URL}/api/property-settings/${user.property_ids[0]}`, {
        tax_type: taxType || null,
        tax_rate: rate || null,
        tax_on_by_default: taxOnByDefault,
        freight_allocation_method: freightMethod,
        weight_unit: weightUnit,
        volume_unit: volumeUnit
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tax settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tax settings');
    } finally {
      setTaxSaving(false);
    }
  };

  const fetchOOOSettings = async () => {
    setOooLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/user/out-of-office`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setOooActive(data.is_active || false);
      setOooStartDate(data.start_date || '');
      setOooEndDate(data.end_date || '');
      setOooDelegate(data.delegate_user_id || '');
      setOooReason(data.reason || '');
    } catch (error) {
      console.error('Failed to fetch OOO settings:', error);
    } finally {
      setOooLoading(false);
    }
  };

  const fetchDelegates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/delegates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDelegates(response.data);
    } catch (error) {
      console.error('Failed to fetch delegates:', error);
    }
  };

  const handleSaveOOO = async () => {
    // Validate if activating
    if (oooActive) {
      if (!oooStartDate || !oooEndDate) {
        toast.error('Please select start and end dates');
        return;
      }
      if (!oooDelegate) {
        toast.error('Please select a delegate to handle approvals');
        return;
      }
      if (new Date(oooEndDate) < new Date(oooStartDate)) {
        toast.error('End date must be after start date');
        return;
      }
    }

    setOooSaving(true);
    try {
      await axios.put(`${API_URL}/api/user/out-of-office`, {
        is_active: oooActive,
        start_date: oooActive ? oooStartDate : null,
        end_date: oooActive ? oooEndDate : null,
        delegate_user_id: oooActive ? oooDelegate : null,
        reason: oooReason || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(oooActive ? 'Out of Office activated' : 'Out of Office deactivated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Out of Office settings');
    } finally {
      setOooSaving(false);
    }
  };

  const handleSaveCurrency = async () => {
    setSaving(true);
    try {
      await updateCurrency(selectedCurrency);
      toast.success('Currency preference updated');
    } catch (error) {
      toast.error('Failed to update currency');
    } finally {
      setSaving(false);
    }
  };

  const CURRENCY_INFO = {
    USD: { name: 'US Dollar', symbol: '$', rate: '1.00' },
    XCD: { name: 'Eastern Caribbean Dollar', symbol: 'EC$', rate: '2.70' },
    JMD: { name: 'Jamaican Dollar', symbol: 'J$', rate: '155.00' },
    TTD: { name: 'Trinidad & Tobago Dollar', symbol: 'TT$', rate: '6.78' }
  };

  const getRoleLabel = (role) => {
    const labels = {
      purchasing_manager: 'Purchasing Manager',
      department_head: 'Department Head',
      financial_controller: 'Financial Controller'
    };
    return labels[role] || role?.replace('_', ' ');
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Settings
        </h1>
        <p className="text-slate-500 mt-1">Manage your account preferences</p>
      </div>

      {/* Change Password Section — self-service password change for logged-in users */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-testid="change-password-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#3D1D72]/10 flex items-center justify-center">
            <Key size={20} weight="duotone" className="text-[#3D1D72]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Change Password
            </h2>
            <p className="text-sm text-slate-500">Update your sign-in password. Requires your current one.</p>
          </div>
        </div>
        <form onSubmit={handleChangeMyPassword} className="space-y-4 max-w-md">
          <div>
            <Label htmlFor="cp-current">Current password</Label>
            <Input
              id="cp-current" type="password" value={cpCurrent}
              onChange={(e) => setCpCurrent(e.target.value)}
              autoComplete="current-password" required
              data-testid="change-password-current"
            />
          </div>
          <div>
            <Label htmlFor="cp-new">New password (min 8 chars)</Label>
            <Input
              id="cp-new" type="password" value={cpNew}
              onChange={(e) => setCpNew(e.target.value)}
              minLength={8} autoComplete="new-password" required
              data-testid="change-password-new"
            />
          </div>
          <div>
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm" type="password" value={cpConfirm}
              onChange={(e) => setCpConfirm(e.target.value)}
              minLength={8} autoComplete="new-password" required
              data-testid="change-password-confirm"
            />
          </div>
          <Button
            type="submit"
            disabled={cpSaving || !cpCurrent || !cpNew || !cpConfirm}
            className="bg-[#3D1D72] hover:bg-[#2D1656] text-white"
            data-testid="change-password-submit"
          >
            {cpSaving ? 'Updating…' : 'Update password'}
          </Button>
        </form>
      </div>

      {/* Out of Office Section - Only for PM, DH, FC */}
      {isApprover && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-testid="ooo-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
              <Airplane size={20} weight="duotone" className="text-[#F59E0B]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Out of Office
              </h2>
              <p className="text-sm text-slate-500">Delegate approvals while you're away</p>
            </div>
          </div>

          {oooLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size={24} className="animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* OOO Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${oooActive ? 'bg-amber-500 animate-pulse' : 'bg-slate-300'}`} />
                  <div>
                    <p className="font-medium text-slate-800">Out of Office Status</p>
                    <p className="text-sm text-slate-500">
                      {oooActive ? 'Currently active - approvals delegated' : 'Not active'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={oooActive}
                  onCheckedChange={setOooActive}
                  data-testid="ooo-toggle"
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <CalendarBlank size={16} className="text-slate-400" />
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    value={oooStartDate}
                    onChange={(e) => setOooStartDate(e.target.value)}
                    disabled={!oooActive}
                    data-testid="ooo-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <CalendarBlank size={16} className="text-slate-400" />
                    End Date
                  </Label>
                  <Input
                    type="date"
                    value={oooEndDate}
                    onChange={(e) => setOooEndDate(e.target.value)}
                    disabled={!oooActive}
                    min={oooStartDate}
                    data-testid="ooo-end-date"
                  />
                </div>
              </div>

              {/* Delegate Selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <UserSwitch size={16} className="text-slate-400" />
                  Delegate Approvals To
                </Label>
                <Select 
                  value={oooDelegate} 
                  onValueChange={setOooDelegate}
                  disabled={!oooActive}
                >
                  <SelectTrigger data-testid="ooo-delegate-select">
                    <SelectValue placeholder="Select a delegate..." />
                  </SelectTrigger>
                  <SelectContent>
                    {delegates.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        <div className="flex items-center gap-2">
                          <span>{d.full_name}</span>
                          <span className="text-xs text-slate-400">({getRoleLabel(d.role)})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  This person will receive and handle approval requests on your behalf
                </p>
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <Label>Reason (Optional)</Label>
                <Textarea
                  value={oooReason}
                  onChange={(e) => setOooReason(e.target.value)}
                  placeholder="e.g., Annual leave, Conference, Business trip..."
                  rows={2}
                  disabled={!oooActive}
                  data-testid="ooo-reason"
                />
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSaveOOO}
                disabled={oooSaving}
                className="bg-[#F59E0B] hover:bg-[#D97706]"
                data-testid="save-ooo-btn"
              >
                {oooSaving ? (
                  <Spinner size={20} className="animate-spin mr-2" />
                ) : (
                  <CheckCircle size={20} className="mr-2" />
                )}
                Save Out of Office Settings
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Pilot Mode Section - Hotel Admin / PM only */}
      {isHotelAdmin && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-testid="pilot-mode-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#3D1D72]/10 flex items-center justify-center">
              <Gear size={20} weight="duotone" className="text-[#3D1D72]" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Pilot Mode
              </h2>
              <p className="text-sm text-slate-500">
                Run HERChain for one department only during a trial. When enabled, all workflows are restricted to the selected department.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50">
              <div>
                <Label className="text-sm font-medium text-slate-800">Pilot Mode</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  {pilotEnabled ? <span className="text-[#3D1D72] font-semibold">ACTIVE</span> : 'Off — all departments available'}
                </p>
              </div>
              <Switch
                checked={pilotEnabled}
                onCheckedChange={(val) => {
                  if (val && !pilotDepartment) {
                    toast.error('Select a department first');
                    return;
                  }
                  handleSavePilot(val, pilotDepartment);
                }}
                disabled={pilotSaving}
                data-testid="pilot-toggle"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-800">Pilot Department</Label>
              <Select
                value={pilotDepartment || undefined}
                onValueChange={(val) => {
                  setPilotDepartment(val);
                  // If pilot already enabled, save the new department immediately.
                  if (pilotEnabled) handleSavePilot(true, val);
                }}
                disabled={pilotSaving}
              >
                <SelectTrigger data-testid="pilot-department-select">
                  <SelectValue placeholder="Select department for pilot" />
                </SelectTrigger>
                <SelectContent>
                  {pilotDepartmentOptions.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Only this department's PRs, POs, vendors, shipments, and receiving records will be visible across the platform.
              </p>
            </div>
          </div>

          {pilotEnabled && (
            <div className="mt-4 rounded-lg border border-[#3D1D72]/20 bg-[#F0EBF8] px-4 py-3 text-sm text-[#3D1D72]" data-testid="pilot-active-notice">
              <span className="font-semibold">Pilot Mode Active.</span>{' '}
              Only the <span className="font-semibold">{pilotDepartment || '—'}</span> department is visible.
              Turn the toggle off here to resume normal multi-department operation.
            </div>
          )}
        </div>
      )}

      {/* Tax Settings Section - Only for PM and FC */}
      {canEditTaxSettings && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-testid="tax-settings-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
              <Receipt size={20} weight="duotone" className="text-[#10B981]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Tax Settings
              </h2>
              <p className="text-sm text-slate-500">Configure tax for Local Purchase Orders</p>
            </div>
          </div>

          {taxLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size={24} className="animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Tax Type */}
              <div className="space-y-2">
                <Label htmlFor="tax-type">Tax Type</Label>
                <Input
                  id="tax-type"
                  value={taxType}
                  onChange={(e) => setTaxType(e.target.value)}
                  placeholder="e.g., ABST, VAT, Sales Tax"
                  data-testid="tax-type-input"
                />
                <p className="text-xs text-slate-500">
                  The name of the tax applicable to your property (e.g., Antigua ABST, Barbados VAT)
                </p>
              </div>

              {/* Tax Rate */}
              <div className="space-y-2">
                <Label htmlFor="tax-rate">Tax Rate (%)</Label>
                <div className="relative">
                  <Input
                    id="tax-rate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    placeholder="e.g., 17"
                    className="pr-10"
                    data-testid="tax-rate-input"
                  />
                  <Percent size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
                <p className="text-xs text-slate-500">
                  Common rates: Antigua 17%, St Lucia 12.5%, Barbados 17.5%, St Vincent 16%, Grenada 15%
                </p>
              </div>

              {/* Tax On By Default Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${taxOnByDefault ? 'bg-green-500' : 'bg-slate-300'}`} />
                  <div>
                    <p className="font-medium text-slate-800">Apply Tax by Default</p>
                    <p className="text-sm text-slate-500">
                      {taxOnByDefault 
                        ? 'Tax will be automatically applied to new Local POs' 
                        : 'Tax will not be applied by default (can be toggled on per PO)'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={taxOnByDefault}
                  onCheckedChange={setTaxOnByDefault}
                  data-testid="tax-default-toggle"
                />
              </div>

              {/* Info Box */}
              <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Tax only applies to Local POs. Overseas POs use a separate landed cost system 
                  with freight, customs duty, and broker fees calculated at Goods Received.
                </p>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSaveTaxSettings}
                disabled={taxSaving}
                className="bg-[#10B981] hover:bg-[#059669]"
                data-testid="save-tax-btn"
              >
                {taxSaving ? (
                  <Spinner size={20} className="animate-spin mr-2" />
                ) : (
                  <CheckCircle size={20} className="mr-2" />
                )}
                Save Tax Settings
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Freight Allocation Settings - Only for PM and FC */}
      {canEditTaxSettings && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-testid="freight-allocation-settings">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Truck size={20} weight="duotone" className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Freight Allocation Settings
              </h2>
              <p className="text-sm text-slate-500">Configure how landed cost charges are allocated across items</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="font-medium">Freight Allocation Method</Label>
              <div className="space-y-2">
                <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-slate-50 transition-colors"
                  data-testid="alloc-by-value">
                  <input type="radio" name="freightMethod" value="by_value"
                    checked={freightMethod === 'by_value'}
                    onChange={e => setFreightMethod(e.target.value)} className="mt-1" />
                  <div>
                    <p className="font-medium text-slate-800">Allocate by Value</p>
                    <p className="text-sm text-slate-500">Freight allocated proportionally based on each item's value as a percentage of total shipment value. No additional data entry required.</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-slate-50 transition-colors"
                  data-testid="alloc-by-volume">
                  <input type="radio" name="freightMethod" value="by_volume"
                    checked={freightMethod === 'by_volume'}
                    onChange={e => setFreightMethod(e.target.value)} className="mt-1" />
                  <div>
                    <p className="font-medium text-slate-800">Allocate by Volume / Weight</p>
                    <p className="text-sm text-slate-500">Freight allocated based on weight or volume. Receiver enters weight/volume per item at receiving. All other charges (duty, broker, trucking) still by value.</p>
                  </div>
                </label>
              </div>
            </div>
            
            {freightMethod === 'by_volume' && (
              <div className="grid grid-cols-2 gap-4 pl-6">
                <div className="space-y-2">
                  <Label>Weight Unit</Label>
                  <select value={weightUnit} onChange={e => setWeightUnit(e.target.value)}
                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="weight-unit-select">
                    <option value="kg">Kilograms (kg)</option>
                    <option value="lbs">Pounds (lbs)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Volume Unit</Label>
                  <select value={volumeUnit} onChange={e => setVolumeUnit(e.target.value)}
                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="volume-unit-select">
                    <option value="cbm">Cubic Metres (cbm)</option>
                    <option value="cft">Cubic Feet (cft)</option>
                  </select>
                </div>
              </div>
            )}
            
            <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
              <p><strong>Note:</strong> All other charges (customs duty, broker fees, trucking, insurance) are always allocated by value regardless of this setting.</p>
            </div>
          </div>
        </div>
      )}


      {/* Profile Section */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#0F52BA]/10 flex items-center justify-center">
            <User size={20} weight="duotone" className="text-[#0F52BA]" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Profile Information
          </h2>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-500 mb-1">Full Name</p>
              <p className="font-medium text-slate-800">{user?.full_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Email</p>
              <p className="font-medium text-slate-800">{user?.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-500 mb-1">Role</p>
              <p className="font-medium text-slate-800 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Subscription Tier</p>
              <p className="font-medium text-slate-800 capitalize">{user?.subscription_tier?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Currency Settings */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#38B2AC]/10 flex items-center justify-center">
            <CurrencyDollar size={20} weight="duotone" className="text-[#38B2AC]" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Currency Preferences
          </h2>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Preferred Display Currency</Label>
            <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
              <SelectTrigger className="w-full max-w-xs" data-testid="currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD - US Dollar ($)</SelectItem>
                <SelectItem value="XCD">XCD - Eastern Caribbean Dollar (EC$)</SelectItem>
                <SelectItem value="JMD">JMD - Jamaican Dollar (J$)</SelectItem>
                <SelectItem value="TTD">TTD - Trinidad & Tobago Dollar (TT$)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Currency Info Card */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm font-medium text-slate-700 mb-2">Selected Currency Details</p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Name</p>
                <p className="font-medium">{CURRENCY_INFO[selectedCurrency]?.name}</p>
              </div>
              <div>
                <p className="text-slate-500">Symbol</p>
                <p className="font-medium">{CURRENCY_INFO[selectedCurrency]?.symbol}</p>
              </div>
              <div>
                <p className="text-slate-500">Rate vs USD</p>
                <p className="font-medium">{CURRENCY_INFO[selectedCurrency]?.rate}</p>
              </div>
            </div>
          </div>

          <Button
            onClick={handleSaveCurrency}
            disabled={saving || selectedCurrency === user?.preferred_currency}
            className="bg-[#0F52BA] hover:bg-[#0A3D8F]"
            data-testid="save-currency-btn"
          >
            {saving ? (
              <Spinner size={20} className="animate-spin mr-2" />
            ) : (
              <CheckCircle size={20} className="mr-2" />
            )}
            Save Currency Preference
          </Button>
        </div>
      </div>

      {/* System Info */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#F4A261]/10 flex items-center justify-center">
            <Gear size={20} weight="duotone" className="text-[#F4A261]" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            System Information
          </h2>
        </div>

        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Platform Version</span>
            <span className="font-medium text-slate-800">2.5.0</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Supported Currencies</span>
            <span className="font-medium text-slate-800">USD, XCD, JMD, TTD</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Modules Active</span>
            <span className="font-medium text-slate-800">5 of 5</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-slate-500">Support</span>
            <span className="font-medium text-[#0F52BA]">support@herchain.com</span>
          </div>
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-[#0F52BA]/10 to-[#38B2AC]/10 rounded-lg">
          <p className="text-sm text-slate-600">
            <strong>HERChain Solutions</strong> - Purpose-built procurement platform for Caribbean hospitality.
            Streamlining supply chains from requisition to delivery.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
